import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Field } from '@/components/common/Field'
import { inputClass, primaryButtonClass, selectClass } from '@/components/common/formStyles'
import { createAccount, listAccounts } from '@/lib/accountsApi'
import { useCreateExternalAsset } from '@/lib/useExternalAssets'
import type { Account } from '@/types/account'
import type { AssetClass, ExternalAsset, UpdateFrequency } from '@/types/externalAsset'

const ASSET_CLASS_LABELS: Record<AssetClass, string> = {
  crypto: 'Crypto (BTC, ETH …)',
  real_estate: 'Immobilie',
  commodity_physical: 'Commodity physisch (Gold/Silber)',
  cash: 'Cash (Tagesgeld/Konto)',
  other: 'Sonstiges',
}

const UPDATE_FREQUENCY_LABELS: Record<UpdateFrequency, string> = {
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
  on_demand: 'Bei Bedarf',
}

interface ExternalAssetFormProps {
  /** Wird nach erfolgreichem Create aufgerufen — für Modal-Close oder Reset. */
  onSuccess?: (asset: ExternalAsset) => void
  /** Standard `submit-Button`. Auf `false` setzen, wenn die Form Teil eines
   *  größeren Layouts ist und der Submit anders aussehen soll. */
  showSubmitButton?: boolean
  submitLabel?: string
}

/**
 * Wiederverwendbare Form für External-Asset-Anlage. Fängt selbst den Fall
 * "kein external-Account vorhanden" ab und bietet eine 1-Klick-Auto-Anlage
 * eines Standard-Containers an.
 */
export function ExternalAssetForm({
  onSuccess,
  showSubmitButton = true,
  submitLabel,
}: ExternalAssetFormProps) {
  const queryClient = useQueryClient()
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts })
  const createAsset = useCreateExternalAsset()

  const externalAccounts: Account[] = useMemo(
    () => (accountsQuery.data ?? []).filter((a) => a.broker_type === 'external'),
    [accountsQuery.data],
  )

  const [accountId, setAccountId] = useState<number | null>(null)
  const [name, setName] = useState('Bitcoin (CoinTracking)')
  const [assetClass, setAssetClass] = useState<AssetClass>('crypto')
  const [valueEur, setValueEur] = useState('')
  const [updateFrequency, setUpdateFrequency] = useState<UpdateFrequency>('monthly')
  const [source, setSource] = useState('CoinTracking')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (accountId === null && externalAccounts.length > 0) {
      setAccountId(externalAccounts[0].id)
    }
  }, [accountId, externalAccounts])

  const createExternalAccount = useMutation({
    mutationFn: () =>
      createAccount({
        name: 'Externe Vermögenswerte',
        broker_type: 'external',
        currency: 'EUR',
      }),
    onSuccess: (account) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      setAccountId(account.id)
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (accountId === null) {
      setError('Erst einen external-Account anlegen.')
      return
    }
    const trimmedValue = valueEur.trim()
    if (!trimmedValue) {
      setError('EUR-Wert eingeben.')
      return
    }
    createAsset.mutate(
      {
        account_id: accountId,
        name: name.trim(),
        asset_class: assetClass,
        current_value_eur: trimmedValue,
        update_frequency: updateFrequency,
        source: source.trim() || null,
      },
      {
        onSuccess: (asset) => {
          setName('Bitcoin (CoinTracking)')
          setAssetClass('crypto')
          setValueEur('')
          setUpdateFrequency('monthly')
          setSource('CoinTracking')
          onSuccess?.(asset)
        },
        onError: (err) => {
          if (isAxiosError(err) && err.response?.status === 422) {
            setError('Eingaben prüfen (EUR-Wert ≥ 0, Asset-Class gültig).')
          } else if (isAxiosError(err) && err.response?.status === 400) {
            setError('Account ungültig oder kein external-Account.')
          } else {
            setError('Fehler beim Anlegen. Erneut versuchen.')
          }
        },
      },
    )
  }

  if (externalAccounts.length === 0) {
    return (
      <section className="border border-bernstein/30 bg-bernstein/5 px-5 py-4">
        <p className="tracked-caps text-[10px] text-bernstein">External-Account fehlt</p>
        <p className="mt-2 font-mono text-sm leading-6 text-ink">
          External Assets brauchen einen external-Account als Container. Lege einen Standard-Account
          an oder definiere eigene Accounts auf der Wizard-Seite.
        </p>
        <button
          type="button"
          onClick={() => createExternalAccount.mutate()}
          disabled={createExternalAccount.isPending}
          className={`${primaryButtonClass} mt-4`}
        >
          {createExternalAccount.isPending
            ? 'Lege an…'
            : 'Standard-Account "Externe Vermögenswerte" anlegen'}
        </button>
      </section>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
      {externalAccounts.length > 1 && (
        <Field label="Account">
          <select
            value={accountId ?? ''}
            onChange={(e) => setAccountId(Number(e.target.value))}
            className={selectClass}
          >
            {externalAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={128}
          required
          className={inputClass}
        />
      </Field>

      <Field label="Asset-Class">
        <select
          value={assetClass}
          onChange={(e) => setAssetClass(e.target.value as AssetClass)}
          className={selectClass}
        >
          {Object.entries(ASSET_CLASS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="EUR-Wert (aktuell)">
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={valueEur}
          onChange={(e) => setValueEur(e.target.value)}
          required
          placeholder="z.B. 12500.00"
          className={`${inputClass} font-mono`}
        />
      </Field>

      <Field label="Update-Frequenz">
        <select
          value={updateFrequency}
          onChange={(e) => setUpdateFrequency(e.target.value as UpdateFrequency)}
          className={selectClass}
        >
          {Object.entries(UPDATE_FREQUENCY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Quelle (optional)">
        <input
          value={source}
          onChange={(e) => setSource(e.target.value)}
          maxLength={64}
          placeholder="z.B. CoinTracking, Notar-Akte"
          className={inputClass}
        />
      </Field>

      {error && (
        <p role="alert" className="font-mono text-xs text-loss">
          {error}
        </p>
      )}

      {showSubmitButton && (
        <button type="submit" disabled={createAsset.isPending} className={primaryButtonClass}>
          {createAsset.isPending ? 'Speichert…' : (submitLabel ?? 'Asset anlegen')}
        </button>
      )}
    </form>
  )
}
